// ============================================
// components/external/ExternalLayout.tsx
// Lightweight shell for Society and Supplier portal users — mobile-responsive
// with hamburger-toggled sidebar.
// ============================================
import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, NavLink, Link, useLocation } from 'react-router-dom';
import { LogOut, KeyRound, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/markfed';

export interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  end?: boolean;
}

interface Props {
  allowed: UserRole[];
  title: string;
  accent?: 'green' | 'amber';
  nav?: NavItem[];
}

export const ExternalLayout: React.FC<Props> = ({ allowed, title, accent = 'green', nav = [] }) => {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer when navigation changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-14 h-14 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  const canUseThis = user.role === UserRole.SUPER_ADMIN
    || user.role === UserRole.MD
    || allowed.includes(user.role);

  if (!canUseThis) {
    return <Navigate to="/dashboard" replace />;
  }

  const bannerColor = accent === 'amber' ? 'bg-amber-600' : 'bg-emerald-700';
  const sidebarActive = accent === 'amber'
    ? 'bg-amber-50 text-amber-800 border-l-4 border-amber-600'
    : 'bg-emerald-50 text-emerald-800 border-l-4 border-emerald-600';

  const renderNav = () => (
    <nav className="py-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-2.5 text-sm transition border-l-4 ${
              isActive ? sidebarActive : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'
            }`
          }
        >
          {item.icon}
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className={`${bannerColor} text-white shadow sticky top-0 z-30`}>
        <div className="px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {nav.length > 0 && (
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-1.5 rounded hover:bg-white/10"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <img
              src={process.env.PUBLIC_URL + '/markfed-logo.png'}
              alt="TG MARKFED"
              className="w-7 h-7 sm:w-8 sm:h-8 object-contain bg-white rounded p-0.5 shrink-0"
            />
            <div className="min-w-0">
              <div className="font-semibold text-xs sm:text-sm leading-tight truncate">TG MARKFED — {title}</div>
              <div className="text-[10px] sm:text-xs opacity-80 truncate">Signed in as {user.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link to="/change-password"
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-[11px] sm:text-xs bg-white/10 hover:bg-white/20 rounded transition">
              <KeyRound className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Change Password</span>
            </Link>
            <button onClick={logout}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-[11px] sm:text-xs bg-white/10 hover:bg-white/20 rounded transition">
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 relative">
        {/* Desktop sidebar */}
        {nav.length > 0 && (
          <aside className="hidden md:block w-56 bg-white border-r border-gray-200 shrink-0">
            {renderNav()}
          </aside>
        )}

        {/* Mobile drawer + backdrop */}
        {nav.length > 0 && mobileOpen && (
          <>
            <div
              className="md:hidden fixed inset-0 bg-black/40 z-40"
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <aside className="md:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 shadow-xl flex flex-col">
              <div className={`${bannerColor} text-white px-4 py-3 flex items-center justify-between`}>
                <span className="font-semibold text-sm">{title}</span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1 rounded hover:bg-white/10"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto flex-1">
                {renderNav()}
              </div>
            </aside>
          </>
        )}

        <main className="flex-1 px-3 sm:px-4 py-4 sm:py-6 max-w-6xl mx-auto w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ExternalLayout;
